import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import ErrorIcon from "@mui/icons-material/Error";
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ProjectEpsgAutoFill,
  ProjectEpsgAutoFillProps,
} from "./ProjectEpsgAutoFill";
import { ReceivedFiles } from "./ProjectUploadContent";

export type SetProjectionType = (
  projection:
    | (number | null)
    | ((prevProjection: number | null) => number | null),
) => void;

type SetFilesType = (
  files: ReceivedFiles | ((prevFiles: ReceivedFiles) => ReceivedFiles),
) => void;

type RequiredUploadFileBoxProps = {
  allowedFiles: ReceivedFiles;
  invalidFiles: ReceivedFiles;
  setAllowedFiles: SetFilesType;
  setInvalidFiles: SetFilesType;
  invalidFileAmount: boolean;
  // setFiles: (files: File[] | ((prevFiles: File[]) => File[])) => void;
} & ProjectEpsgAutoFillProps;

export const UploadedFilesContainer = ({
  allowedFiles,
  invalidFiles,
  setAllowedFiles,
  setInvalidFiles,
  invalidFileAmount,
  ...rest
}: RequiredUploadFileBoxProps) => {
  const { t } = useTranslation();

  const { projectionId } = rest;

  const showTooltip = useMemo(() => {
    return (
      Object.entries(allowedFiles).some(([, value]) => {
        return value.files.length !== 0;
      }) || projectionId !== null
    );
  }, [allowedFiles, projectionId]);

  const missingEpsg = React.useMemo(() => {
    return !projectionId && allowedFiles.prj.files.length === 0;
  }, [allowedFiles.prj.files.length, projectionId]);

  return (
    <Box sx={{ padding: "5px" }}>
      <Box
        sx={{
          display: "grid",
          gap: "10px",
          gridTemplateColumns: "45% 45%",
        }}
      >
        <UploadedFileContainer
          //    title={t("projects:requiredFiles")}
          hideTooltip={!showTooltip}
          setFiles={setAllowedFiles}
          receivedFiles={allowedFiles}
          isAllowedFileContainer
          invalidFileAmount={invalidFileAmount}
          missingEpsg={missingEpsg}
          {...rest}
        />

        <UploadedFileContainer
          title={t("projects:invalidFiles")}
          receivedFiles={invalidFiles}
          hideTooltip
          setFiles={setInvalidFiles}
          {...rest}
        />
      </Box>
    </Box>
  );
};

type UploadedFileContainerProps = {
  receivedFiles: ReceivedFiles;
  setFiles?: SetFilesType;
  title?: string;
  hideTooltip?: boolean;
  isAllowedFileContainer?: boolean;
  invalidFileAmount?: boolean;
  missingEpsg?: boolean;
} & ProjectEpsgAutoFillProps;

function UploadedFileContainer({
  receivedFiles,
  title,
  invalidFileAmount,
  hideTooltip,
  missingEpsg,
  ...rest
}: UploadedFileContainerProps) {
  const fileExtensions = Object.keys(receivedFiles);
  if (fileExtensions.length < 1) return null;
  return (
    <Paper
      sx={{
        position: "relative",
        p: "10px",
        //  border: isInvalid ? "1px solid red" : "1px solid green",
        minHeight: "10px",
        display: "grid",
        gridTemplateColumns: `auto min-content`,
      }}
    >
      <Box>
        {title && <Typography variant="h6">{title}</Typography>}
        <Box sx={{ display: "grid", gap: "10px" }}>
          {fileExtensions.map((extension, i) => (
            <UploadedFileExtensionBox
              key={extension}
              extension={extension}
              files={receivedFiles[extension]?.files ?? []}
              order={i}
              {...rest}
            />
          ))}
        </Box>
      </Box>
      <FileStateIcon
        missingEpsg={missingEpsg}
        hideTooltip={hideTooltip}
        invalidFiles={invalidFileAmount}
      />
    </Paper>
  );
}

type UploadFileBoxProps = {
  extension: string;
  files: File[];
  setFiles?: SetFilesType;

  isAllowedFileContainer?: boolean;
  order: number;
} & ProjectEpsgAutoFillProps;
function UploadedFileExtensionBox({
  files,
  extension,
  setFiles,

  isAllowedFileContainer,
  order,
  ...restProps
}: UploadFileBoxProps) {
  const fileBox = React.useMemo(() => {
    if (extension === "prj" && files.length === 0)
      return <ProjectEpsgAutoFill {...restProps} />;
    const boxes = files.map((f) => {
      return (
        <Box key={f.name}>
          {f.name}
          {setFiles && (
            <IconButton
              onClick={() =>
                setFiles((prevF) => {
                  if (
                    prevF[extension].files.length < 2 &&
                    !isAllowedFileContainer
                  ) {
                    const { [extension]: ext, ...rest } = prevF;
                    return { ...rest };
                  }
                  const modifiedFiles = {
                    ...prevF,

                    [extension]: {
                      files: prevF[extension].files.filter(
                        (file) => file.name !== f.name,
                      ),
                    },
                  };
                  return modifiedFiles;
                })
              }
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      );
    });
    return (
      <Box
        key={extension}
        sx={{
          //  border: isInvalid ? "1px solid red" : "1px solid green",
          minHeight: "10px",
          position: "relative",
          display: "grid",
          gridTemplateColumns: `auto min-content`,
          alignItems: "center",
        }}
      >
        <Box>{boxes ?? null}</Box>
      </Box>
    );
  }, [extension, files, isAllowedFileContainer, restProps, setFiles]);

  return (
    <>
      {order !== 0 && <Divider />}
      <Typography fontWeight="500" variant="body1">
        {extension}
      </Typography>
      <Box sx={{ display: "grid", gridAutoFlow: "row" }}>{fileBox}</Box>
    </>
  );
}

export function FileStateIcon({
  invalidFiles,
  hideTooltip,
  isVisible = true,
  missingEpsg,
}: {
  invalidFiles?: boolean | undefined;
  hideTooltip?: boolean;
  isVisible?: boolean | undefined;
  missingEpsg?: boolean;
}) {
  const { t } = useTranslation();
  if (hideTooltip || !isVisible) return null;
  const isInvalid = missingEpsg || invalidFiles;
  return (
    <Box>
      {isInvalid ? (
        <Tooltip
          title={
            (invalidFiles
              ? t("projects:shapeFileUploadTooltip")
              : t("projects:missingEpsg")) ?? ""
          }
        >
          <ErrorIcon color="error" />
        </Tooltip>
      ) : (
        <DoneIcon color="success" />
      )}
    </Box>
  );
}
